<planning-top-nav></planning-top-nav>

<div class="work-orders-assign-container">
    <!-- Loading -->
    <div *ngIf="isLoading" class="spinner-container">
        <div class="spinner"></div>
    </div>

    <!-- Error -->
    <div *ngIf="errorMessage" class="error-message">
        {{ errorMessage }}
    </div>

    <!-- PAGE CONTENT WRAPPER (so we can blur/disable EVERYTHING when confirmOpen/resultsOpen) -->
    <div class="page-content" [class.is-dimmed]="confirmOpen || resultsOpen">
        <!-- Header -->
        <div class="header" *ngIf="!isLoading">
            <div class="header-left">
                <button type="button" class="btn btn-primary" (click)="backToWorkOrders()"> ← Back </button>

                <div class="title-wrap">
                    <h2>Assign Work Orders</h2>
                    <p class="subtitle">
                        Drag a work order onto employee rows, or select employees and drop a work order into the drop
                        zone.
                        Then Save & Confirm.
                    </p>
                </div>
            </div>

            <!-- Action bundle: only show what can be clicked -->
            <div class="header-actions">
                <!-- Clear Selection: only when filters/search/selection exist -->
                <button *ngIf="
            employeeSearch.trim().length > 0 ||
            selectedDepartmentIds.length > 0 ||
            selectedEmployeesCount > 0 ||
            workOrderSearch.trim().length > 0 ||
            selectedWorkOrderDepartmentIds.length > 0 ||
            selectedWorkOrderYears.length > 0 ||
            overheadOnly
          " type="button" class="btn btn-secondary" (click)="clearAllSelectionsAndFilters()">
                    Clear Selection
                </button>

                <!-- Queue / Save: only when there is queued work -->
                <ng-container *ngIf="pendingAssignments.length > 0">
                    <button type="button" class="btn btn-secondary" (click)="clearPending()" [disabled]="isSaving">
                        Clear Queue ({{ pendingAssignments.length }})
                    </button>

                    <button type="button" class="btn btn-primary" (click)="openConfirm()" [disabled]="isSaving">
                        Save Assignments ({{ pendingAssignments.length }})
                    </button>
                </ng-container>
            </div>
        </div>

        <!-- Main Layout -->
        <div class="assign-grid" *ngIf="!isLoading">
            <!-- Employees -->
            <div class="panel employees-panel">
                <div class="panel-header">
                    <div class="panel-title">
                        <h3>Employees</h3>
                        <div class="hint">Click to select. Drop a work order on a row to queue.</div>
                    </div>

                    <!-- Employees filters row -->
                    <div class="filters employees-filters">
                        <div class="filter">
                            <label>Search</label>
                            <input type="text" placeholder="Name or employee #" [(ngModel)]="employeeSearch" />
                        </div>

                        <!-- Dept + Select All toggle aligned -->
                        <div class="filter department-filter">
                            <label>Departments</label>

                            <div class="dept-row">
                                <div class="picker dept-picker">
                                    <button type="button" class="picker-trigger"
                                        (click)="$event.stopPropagation(); showDeptMenu = !showDeptMenu">
                                        <span *ngIf="selectedDepartmentIds.length === 0">All Departments</span>
                                        <span *ngIf="selectedDepartmentIds.length > 0">
                                            {{ selectedDepartmentIds.length }} selected
                                        </span>
                                        <span class="caret">▾</span>
                                    </button>

                                    <div class="picker-menu" *ngIf="showDeptMenu" (click)="$event.stopPropagation()">
                                        <div class="picker-menu-actions">
                                            <button type="button" class="link" (click)="clearDepartmentFilter()">
                                                Clear
                                            </button>
                                        </div>

                                        <div class="picker-options" (scroll)="onDepartmentScroll($event)">
                                            <label class="picker-option" *ngFor="let dept of departments">
                                                <input type="checkbox"
                                                    [checked]="selectedDepartmentIds.includes(dept.id)"
                                                    (change)="toggleDepartmentSelection(dept.id)" />
                                                <span>{{ dept.code }} – {{ dept.name }}</span>
                                            </label>

                                            <div class="loading-inline" *ngIf="isLoadingDepartments">Loading…</div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Select All checkbox (TOGGLES BOTH WAYS) -->
                                <label class="ui-check select-all-toggle"
                                    title="Select all currently filtered employees">
                                    <input type="checkbox" [checked]="
                      filteredEmployees.length > 0 &&
                      selectedEmployeesCount === filteredEmployees.length
                    " [disabled]="filteredEmployees.length === 0" (click)="$event.stopPropagation()"
                                        (change)="toggleSelectAllFilteredEmployees($event.target.checked)" />
                                    <span>Select all</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <!-- Drop zone -->
                    <div class="drop-zone" (dragover)="allowDrop($event)" (drop)="onDropOnSelectedZone($event)">
                        <div class="drop-title">
                            Drop Work Order Here
                            <span class="muted">({{ selectedEmployeesCount }} selected)</span>
                        </div>
                        <div class="drop-subtitle">
                            Select employees, then drop a work order to queue assignments.
                        </div>
                    </div>
                </div>

                <!-- Employees list (infinite scroll) -->
                <div class="panel-body scroll-body" (scroll)="onEmployeesScroll($event)">
                    <div class="employees-list">
                        <div *ngFor="let emp of filteredEmployees" class="employee-row"
                            [class.selected]="isEmployeeSelected(emp.id)" (click)="toggleEmployeeSelected(emp)"
                            (dragover)="allowDrop($event)" (drop)="onDropOnEmployee($event, emp)">
                            <div class="employee-main">
                                <div class="name">{{ emp.firstName }} {{ emp.lastName }}</div>
                                <div class="meta">
                                    <span class="pill">#{{ emp.employeeNumber }}</span>
                                    <span class="pill" *ngIf="emp.department">Dept: {{ emp.department.code }}</span>
                                    <span class="pill" *ngIf="emp.company">{{ emp.company.name }}</span>
                                </div>
                            </div>

                            <span class="check" *ngIf="isEmployeeSelected(emp.id)">✓</span>
                        </div>

                        <div *ngIf="isLoadingEmployees" class="spinner-container">
                            <div class="spinner"></div>
                        </div>
                        <div class="empty" *ngIf="filteredEmployees.length === 0 && !isLoadingEmployees">
                            No employees match your filters.
                        </div>
                    </div>
                </div>
            </div>

            <!-- Work Orders -->
            <div class="panel workorders-panel">
                <div class="panel-header">
                    <div class="panel-title">
                        <h3>Work Orders</h3>
                        <div class="hint">Drag a work order into the employee list or drop zone.</div>
                    </div>

                    <!-- SAME filters structure as Employees -->
                    <div class="filters workorders-filters">
                        <div class="filter">
                            <label>Search</label>
                            <input type="text" placeholder="WON or description" [(ngModel)]="workOrderSearch" />
                        </div>

                        <div class="filter department-filter">
                            <label>Departments</label>

                            <div class="picker">
                                <button type="button" class="picker-trigger"
                                    (click)="$event.stopPropagation(); showWorkOrderDeptMenu = !showWorkOrderDeptMenu">
                                    <span *ngIf="selectedWorkOrderDepartmentIds.length === 0">All Departments</span>
                                    <span *ngIf="selectedWorkOrderDepartmentIds.length > 0">
                                        {{ selectedWorkOrderDepartmentIds.length }} selected
                                    </span>
                                    <span class="caret">▾</span>
                                </button>

                                <div class="picker-menu" *ngIf="showWorkOrderDeptMenu"
                                    (click)="$event.stopPropagation()">
                                    <div class="picker-menu-actions">
                                        <button type="button" class="link"
                                            (click)="clearWorkOrderDeptFilter()">Clear</button>
                                    </div>

                                    <div class="picker-options">
                                        <label class="picker-option" *ngFor="let dept of workOrderDepartments">
                                            <input type="checkbox"
                                                [checked]="selectedWorkOrderDepartmentIds.includes(dept.id)"
                                                (change)="toggleWorkOrderDeptSelection(dept.id)" />
                                            <span>{{ dept.code }} – {{ dept.name }}</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Project -->
                        <div class="filter department-filter">
                            <label>Project</label>

                            <div class="picker">
                                <button type="button" class="picker-trigger"
                                    (click)="$event.stopPropagation(); showWorkOrderYearMenu = !showWorkOrderYearMenu">
                                    <span *ngIf="selectedWorkOrderYears.length === 0">All</span>
                                    <span *ngIf="selectedWorkOrderYears.length > 0">
                                        {{ selectedWorkOrderYears.length }} selected
                                    </span>
                                    <span class="caret">▾</span>
                                </button>

                                <div class="picker-menu" *ngIf="showWorkOrderYearMenu"
                                    (click)="$event.stopPropagation()">
                                    <div class="picker-menu-actions">
                                        <button type="button" class="link"
                                            (click)="clearWorkOrderYearFilter()">Clear</button>
                                    </div>

                                    <div class="picker-options">
                                        <label class="picker-option" *ngFor="let y of workOrderYears">
                                            <input type="checkbox" [checked]="selectedWorkOrderYears.includes(y)"
                                                (change)="toggleWorkOrderYearSelection(y)" />
                                            <span>{{ y }}</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="filter overhead-filter">
                            <label>&nbsp;</label>
                            <!-- SAME checkbox wrapper class as employee select-all so SCSS can match exactly -->
                            <label class="ui-check">
                                <input type="checkbox" [(ngModel)]="overheadOnly" />
                                <span>Overhead only</span>
                            </label>
                        </div>
                    </div>
                </div>

                <!-- Work order list (infinite scroll) -->
                <div class="panel-body scroll-body" (scroll)="onWorkOrdersScroll($event)">
                    <div class="workorders-list">
                        <div *ngFor="let wo of filteredWorkOrders" class="workorder-card" draggable="true"
                            (dragstart)="onDragStartWorkOrder(wo)"
                            [class.dragging]="lastDraggedWorkOrder?.id === wo.id">
                            <div class="won-line">
                                <span class="won">{{ wo.won }}</span>
                                <span class="desc-inline" *ngIf="wo.description">— {{ wo.description }}</span>
                            </div>

                            <div class="meta">
                                <span class="pill" *ngIf="wo.department">Dept: {{ wo.department.code }}</span>
                                <span class="pill" *ngIf="wo.year">Year: {{ wo.year }}</span>
                                <span class="pill" *ngIf="wo.isOverhead">Overhead</span>
                            </div>
                        </div>

                        <div *ngIf="isLoadingWorkOrders" class="spinner-container">
                            <div class="spinner"></div>
                        </div>
                        <div class="empty" *ngIf="filteredWorkOrders.length === 0 && !isLoadingWorkOrders">
                            No work orders match your filters.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Confirm Modal -->
    <div *ngIf="confirmOpen" class="modal-backdrop" (click)="closeConfirm()">
        <div class="modal" (click)="$event.stopPropagation()">
            <h2>Confirm Assignments</h2>

            <p class="modal-subtitle">
                You are about to assign <strong>{{ pendingAssignments.length }}</strong>
                WON{{ pendingAssignments.length === 1 ? '' : 's' }}.
            </p>

            <div class="confirm-table">
                <div class="confirm-row confirm-header">
                    <div class="col emp">Employee</div>
                    <div class="col arrow"></div>
                    <div class="col wons">Work Orders</div>
                </div>

                <div class="confirm-row" *ngFor="let row of confirmRows">
                    <div class="col emp">
                        <div class="emp-name">{{ row.employeeName }}</div>
                        <div class="emp-sub">#{{ row.employeeNumber }}</div>
                    </div>

                    <div class="col arrow">→</div>

                    <div class="col wons">
                        <div class="won-stack">
                            <div class="won-item" *ngFor="let won of row.wons">{{ won }}</div>
                        </div>
                    </div>
                </div>
            </div>



            <div class="modal-buttons">
                <button type="button" class="btn btn-secondary" (click)="closeConfirm()" [disabled]="isSaving">
                    Cancel
                </button>

                <button type="button" class="btn btn-primary" (click)="confirmAndSave()" [disabled]="isSaving">
                    {{ isSaving ? 'Saving...' : 'Yes, Confirm & Save' }}
                </button>
            </div>
        </div>
    </div>

    <!-- Results Modal -->
    <div *ngIf="resultsOpen" class="modal-backdrop" (click)="closeResults()">
        <div class="modal large" (click)="$event.stopPropagation()">
            <h2>Results</h2>

            <div class="results-summary">
                <span class="pill success">Success: {{ successCount }}</span>
                <span class="pill error">Failed: {{ failCount }}</span>
            </div>

            <div class="results-list">
                <div class="results-table-header">
  <div>Work Order</div>
  <div class="col-center">→</div>
  <div>Employee</div>
  <div class="col-right">Status</div>
</div>
                <div *ngFor="let r of assignmentResults" class="result-row" [class.ok]="r.ok" [class.fail]="!r.ok">
                    <div class="left">
                        <div class="top">
                            <span class="won">{{ r.won }}</span>
                            <span class="arrow">→</span>
                            <span class="emp">{{ r.employeeName }} (#{{ r.employeeNumber }})</span>
                        </div>
                        <div class="bottom" *ngIf="!r.ok">
                            <span class="msg">{{ r.statusCode }}: {{ r.message }}</span>
                        </div>
                    </div>

                    <div class="right">
                        <span class="badge" [class.success]="r.ok" [class.error]="!r.ok">
                            {{ r.ok ? 'OK' : 'Failed' }}
                        </span>
                    </div>
                </div>
            </div>

            <div class="modal-buttons">
                <button type="button" class="btn btn-primary" (click)="closeResults()">Close</button>
            </div>
        </div>
    </div>

    <!-- Toast -->
    <div *ngIf="toastVisible" class="toast" [ngClass]="toastType">
        <img class="icon" alt="icon" />
        <span>{{ toastMessage }}</span>
    </div>
</div>